import React, { useEffect, useState } from 'react';
import {
  Table,
  Tabs,
  Button,
  Form,
  Input,
  Select,
  Space,
  Tag,
  Popconfirm,
  Tooltip,
  Switch,
  Row,
  Col,
  Empty,
  Alert,
} from 'antd';
import CBInputNumber from '../../components/CBInputNumber';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, ReloadOutlined,
  CalculatorOutlined,
} from '@ant-design/icons';
import Layout from '../../components/Layout';
import axiosInstance from '../../variables/api/axiosInstance';
import { toast } from 'react-toastify';
import { Trans, useTranslation } from 'react-i18next';
import CBModal from '../../components/CBModal';
const GREEN = '#1a6b3c';

// Les bornes de taux arrivent en DECIMAL(5,2) : « 5.00 » se lit « 5 ».
const nb = (v) => (v == null ? '—' : String(Number(v)));

// ───────────────────────────────────────────────────────────────────
// Configuration des 5 barèmes : chaque entrée décrit les endpoints,
// les colonnes du tableau et les champs du formulaire.
// ───────────────────────────────────────────────────────────────────

const tableBaremes = (t) => ({
  capitalisation: {
    key: 'capitalisation',
    title: t('cima.capitalisation.titre'),
    description: t('cima.capitalisation.description'),
    endpoint: '/v1/baremes/capitalisation',
    idKey: 'id',
    rowKey: 'id',
    columns: [
      { title: t('cima.capitalisation.table'), dataIndex: 'typeBareme', width: 90,
        render: (v) => <Tag color="blue" style={{ fontFamily: 'var(--font-main)', fontWeight: 700, borderRadius: 20 }}>{v}</Tag> },
      { title: t('cima.capitalisation.age'), dataIndex: 'age', width: 80, align: 'center',
        render: (v) => <span style={{ fontWeight: 600 }}>{t('cima.commun.ans', { count: v })}</span> },
      { title: t('cima.capitalisation.prixFrancRente'), dataIndex: 'prixFrancRente', width: 150, align: 'right',
        render: (v) => <span style={{ fontFamily: 'var(--font-main)' }}>{Number(v).toFixed(4)}</span> },
      { title: t('cima.capitalisation.tauxCapit'), dataIndex: 'tauxCapitalisation', width: 130, align: 'center' },
      { title: t('cima.capitalisation.tableMortalite'), dataIndex: 'tableMortalite', width: 140 },
      { title: t('cima.capitalisation.ageLimite'), dataIndex: 'ageLimitePaiement', width: 100, align: 'center',
        render: (v) => t('cima.commun.ans', { count: v }) },
      { title: t('cima.commun.actif'), dataIndex: 'actif', width: 70, align: 'center',
        render: (v) => v ? <Tag color="green" style={{ borderRadius: 20 }}>{t('cima.commun.oui')}</Tag> : <Tag color="red" style={{ borderRadius: 20 }}>{t('cima.commun.non')}</Tag> },
    ],
    fields: (editing) => (
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name="typeBareme" label={t('cima.capitalisation.table')} rules={[{ required: true }]}>
            <Select
              placeholder={t('cima.commun.selectionner')}
              options={['M100', 'F100', 'M25', 'F25'].map(v => ({ value: v, label: v }))}
              getPopupContainer={t => t.parentElement}
            />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="age" label={t('cima.capitalisation.ageSaisie')} rules={[{ required: true }]}>
            <CBInputNumber min={0} max={120} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="prixFrancRente" label={t('cima.capitalisation.prixFrancRente')} rules={[{ required: true }]}>
            <CBInputNumber min={0} step={0.0001} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="tauxCapitalisation" label={t('cima.capitalisation.tauxCapitalisation')} rules={[{ required: true }]}>
            <CBInputNumber min={0} max={100} step={0.01} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="tableMortalite" label={t('cima.capitalisation.tableMortalite')} rules={[{ required: true }]}>
            <Input placeholder="TD 88-90" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="ageLimitePaiement" label={t('cima.capitalisation.ageLimitePaiement')} rules={[{ required: true }]}>
            <CBInputNumber min={0} max={120} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item name="actif" label={t('cima.commun.actif')} valuePropName="checked">
            <Switch checkedChildren={t('cima.commun.oui')} unCheckedChildren={t('cima.commun.non')} />
          </Form.Item>
        </Col>
      </Row>
    ),
    defaults: { actif: true, tauxCapitalisation: 3.25, tableMortalite: 'TD 88-90' },
  },

  'valeur-point-ip': {
    key: 'valeur-point-ip',
    title: t('cima.valeurPointIp.titre'),
    description: t('cima.valeurPointIp.description'),
    endpoint: '/v1/baremes/valeur-point-ip',
    idKey: 'id',
    rowKey: 'id',
    pageSize: 40,
    columns: [
      { title: t('cima.valeurPointIp.trancheAge'), key: 'age', width: 170,
        render: (_, r) => r.ageMax == null
          ? <span style={{ fontWeight: 600 }}>{t('cima.valeurPointIp.ageEtPlus', { min: r.ageMin })}</span>
          : <span style={{ fontWeight: 600 }}>{t('cima.valeurPointIp.ageEntre', { min: r.ageMin, max: r.ageMax })}</span> },
      { title: t('cima.valeurPointIp.tauxIpp'), key: 'ipp', width: 150,
        render: (_, r) => `${nb(r.ippMin)} – ${nb(r.ippMax)} %` },
      { title: t('cima.valeurPointIp.valeurPointColonne'), dataIndex: 'valeurPoint', align: 'right',
        render: (v) => <span style={{ fontWeight: 700, color: GREEN }}>{v} %</span> },
      { title: t('cima.commun.actif'), dataIndex: 'actif', width: 70, align: 'center',
        render: (v) => v ? <Tag color="green" style={{ borderRadius: 20 }}>{t('cima.commun.oui')}</Tag> : <Tag color="red" style={{ borderRadius: 20 }}>{t('cima.commun.non')}</Tag> },
    ],
    fields: () => (
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name="ageMin" label={t('cima.valeurPointIp.ageMin')} rules={[{ required: true }]}>
            <CBInputNumber min={0} max={120} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="ageMax" label={t('cima.valeurPointIp.ageMax')}>
            <CBInputNumber min={0} max={120} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="ippMin" label={t('cima.valeurPointIp.ippMin')} rules={[{ required: true }]}>
            <CBInputNumber min={0} max={100} step={0.5} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="ippMax" label={t('cima.valeurPointIp.ippMax')} rules={[{ required: true }]}>
            <CBInputNumber min={0} max={100} step={0.5} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item name="valeurPoint" label={t('cima.valeurPointIp.valeurPoint')} rules={[{ required: true }]}
            extra={t('cima.valeurPointIp.valeurPointAide')}>
            <CBInputNumber min={0} step={1} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item name="actif" label={t('cima.commun.actif')} valuePropName="checked">
            <Switch checkedChildren={t('cima.commun.oui')} unCheckedChildren={t('cima.commun.non')} />
          </Form.Item>
        </Col>
      </Row>
    ),
    defaults: { actif: true },
  },

  'cle-repartition-265': {
    key: 'cle-repartition-265',
    title: t('cima.cleRepartition.titre'),
    description: t('cima.cleRepartition.description'),
    baremeLegal: t('cima.cleRepartition.baremeLegal'),
    endpoint: '/v1/baremes/cle-repartition-265',
    idKey: 'id',
    rowKey: 'id',
    columns: [
      { title: t('cima.cleRepartition.code'), dataIndex: 'codeSituation', width: 160,
        render: (v) => <Tag color="purple" style={{ fontFamily: 'var(--font-main)', fontWeight: 700, borderRadius: 20 }}>{v}</Tag> },
      { title: t('cima.cleRepartition.situation'), dataIndex: 'libelleSituation', ellipsis: true },
      { title: t('cima.cleRepartition.ascendants'), dataIndex: 'cleAscendants', width: 110, align: 'right',
        render: (v) => `${Number(v || 0)}%` },
      { title: t('cima.cleRepartition.conjoints'), dataIndex: 'cleConjoints', width: 110, align: 'right',
        render: (v) => `${Number(v || 0)}%` },
      { title: t('cima.cleRepartition.enfants'), dataIndex: 'cleEnfants', width: 100, align: 'right',
        render: (v) => `${Number(v || 0)}%` },
      { title: t('cima.cleRepartition.orphelinsDoublesColonne'), dataIndex: 'cleOrphelinsDoubles', width: 120, align: 'right',
        render: (v) => `${Number(v || 0)}%` },
      { title: t('cima.commun.actif'), dataIndex: 'actif', width: 70, align: 'center',
        render: (v) => v ? <Tag color="green" style={{ borderRadius: 20 }}>{t('cima.commun.oui')}</Tag> : <Tag color="red" style={{ borderRadius: 20 }}>{t('cima.commun.non')}</Tag> },
    ],
    fields: () => (
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name="codeSituation" label={t('cima.cleRepartition.codeSituation')}
            rules={[{ required: true }, { pattern: /^[A-Z0-9_]+$/, message: t('cima.cleRepartition.formatCode') }]}>
            <Input placeholder="CONJOINT_ET_ENFANTS" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="libelleSituation" label={t('cima.cleRepartition.libelle')} rules={[{ required: true }]}>
            <Input placeholder={t('cima.cleRepartition.libelleExemple')} />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item name="conditionConjoint" label={t('cima.cleRepartition.condConjoint')} valuePropName="checked">
            <Switch />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item name="conditionEnfant" label={t('cima.cleRepartition.condEnfant')} valuePropName="checked">
            <Switch />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="nombreMaxEnfants" label={t('cima.cleRepartition.nbMaxEnfants')}>
            <CBInputNumber min={0} max={20} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item name="cleAscendants" label={t('cima.cleRepartition.ascendants')}>
            <CBInputNumber min={0} max={100} step={0.5} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item name="cleConjoints" label={t('cima.cleRepartition.conjoints')}>
            <CBInputNumber min={0} max={100} step={0.5} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item name="cleEnfants" label={t('cima.cleRepartition.enfants')}>
            <CBInputNumber min={0} max={100} step={0.5} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item name="cleOrphelinsDoubles" label={t('cima.cleRepartition.orphelinsDoubles')}>
            <CBInputNumber min={0} max={100} step={0.5} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item name="actif" label={t('cima.commun.actif')} valuePropName="checked">
            <Switch checkedChildren={t('cima.commun.oui')} unCheckedChildren={t('cima.commun.non')} />
          </Form.Item>
        </Col>
      </Row>
    ),
    defaults: { actif: true, cleAscendants: 0, cleConjoints: 0, cleEnfants: 0, cleOrphelinsDoubles: 0 },
  },

  'pretium-doloris': {
    key: 'pretium-doloris',
    title: t('cima.pretiumDoloris.titre'),
    description: t('cima.pretiumDoloris.description'),
    endpoint: '/v1/baremes/pretium-doloris',
    idKey: 'id',
    rowKey: 'id',
    columns: [
      { title: t('cima.pretiumDoloris.qualification'), dataIndex: 'qualification', width: 220,
        render: (v) => <Tag color="volcano" style={{ fontFamily: 'var(--font-main)', fontWeight: 700, borderRadius: 20 }}>{v}</Tag> },
      { title: t('cima.pretiumDoloris.points'), dataIndex: 'points', align: 'right',
        render: (v) => <span style={{ fontWeight: 700, color: GREEN }}>{v}%</span> },
      { title: t('cima.pretiumDoloris.moral'), dataIndex: 'moral', width: 90, align: 'center',
        render: (v) => v ? <Tag color="purple" style={{ borderRadius: 20 }}>{t('cima.commun.oui')}</Tag> : <Tag style={{ borderRadius: 20 }}>{t('cima.commun.non')}</Tag> },
      { title: t('cima.commun.actif'), dataIndex: 'actif', width: 70, align: 'center',
        render: (v) => v ? <Tag color="green" style={{ borderRadius: 20 }}>{t('cima.commun.oui')}</Tag> : <Tag color="red" style={{ borderRadius: 20 }}>{t('cima.commun.non')}</Tag> },
    ],
    fields: () => (
      <Row gutter={16}>
        <Col span={14}>
          <Form.Item name="qualification" label={t('cima.pretiumDoloris.qualification')}
            rules={[{ required: true }, { pattern: /^[A-Z_]+$/, message: t('cima.commun.majusculesUnderscores') }]}>
            <Input placeholder="LEGER, MODERE, IMPORTANT…" />
          </Form.Item>
        </Col>
        <Col span={10}>
          <Form.Item name="points" label={t('cima.pretiumDoloris.points')} rules={[{ required: true }]}>
            <CBInputNumber min={0} max={1000} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="moral" label={t('cima.pretiumDoloris.dimensionMorale')} valuePropName="checked">
            <Switch />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="actif" label={t('cima.commun.actif')} valuePropName="checked">
            <Switch checkedChildren={t('cima.commun.oui')} unCheckedChildren={t('cima.commun.non')} />
          </Form.Item>
        </Col>
      </Row>
    ),
    defaults: { actif: true, moral: false },
  },

  'prejudice-moral-266': {
    key: 'prejudice-moral-266',
    title: t('cima.prejudiceMoral.titre'),
    description: t('cima.prejudiceMoral.description'),
    baremeLegal: t('cima.prejudiceMoral.baremeLegal'),
    endpoint: '/v1/baremes/prejudice-moral-266',
    idKey: 'id',
    rowKey: 'id',
    columns: [
      { title: t('cima.prejudiceMoral.lienParenteColonne'), dataIndex: 'lienParente', width: 220,
        render: (v) => <Tag color="orange" style={{ fontFamily: 'var(--font-main)', fontWeight: 700, borderRadius: 20 }}>{v}</Tag> },
      { title: t('cima.prejudiceMoral.cle'), dataIndex: 'cle', align: 'right',
        render: (v) => <span style={{ fontWeight: 700, color: GREEN }}>{Number(v).toFixed(2)}%</span> },
      { title: t('cima.prejudiceMoral.plafond'), dataIndex: 'plafondCategorie',
        render: (v) => v || '-' },
      { title: t('cima.commun.actif'), dataIndex: 'actif', width: 70, align: 'center',
        render: (v) => v ? <Tag color="green" style={{ borderRadius: 20 }}>{t('cima.commun.oui')}</Tag> : <Tag color="red" style={{ borderRadius: 20 }}>{t('cima.commun.non')}</Tag> },
    ],
    fields: () => (
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name="lienParente" label={t('cima.prejudiceMoral.lienParente')}
            rules={[{ required: true }, { pattern: /^[A-Z_]+$/, message: t('cima.commun.majusculesUnderscores') }]}>
            <Input placeholder="CONJOINT, ENFANT_MINEUR, ASCENDANT…" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="cle" label={t('cima.prejudiceMoral.cle')} rules={[{ required: true }]}>
            <CBInputNumber min={0} max={500} step={0.5} style={{ width: '100%' }} />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item name="plafondCategorie" label={t('cima.prejudiceMoral.plafondOptionnel')}>
            <Input placeholder={t('cima.prejudiceMoral.plafondExemple')} />
          </Form.Item>
        </Col>
        <Col span={24}>
          <Form.Item name="actif" label={t('cima.commun.actif')} valuePropName="checked">
            <Switch checkedChildren={t('cima.commun.oui')} unCheckedChildren={t('cima.commun.non')} />
          </Form.Item>
        </Col>
      </Row>
    ),
    defaults: { actif: true },
  },
});

// ───────────────────────────────────────────────────────────────────
// Panneau générique pour un type de barème
// ───────────────────────────────────────────────────────────────────

function BaremePanel({ config }) {
  const { t } = useTranslation('parametres');
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form] = Form.useForm();

  const load = async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(config.endpoint);
      setData(res.data?.data || []);
    } catch {
      toast.error(t('cima.panneau.erreurChargement', { titre: config.title }));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [config.key]);

  const openCreate = () => {
    setEditing(null);
    form.resetFields();
    form.setFieldsValue(config.defaults || {});
    setModalOpen(true);
  };

  const openEdit = (record) => {
    setEditing(record);
    form.resetFields();
    form.setFieldsValue(record);
    setModalOpen(true);
  };

  const onSubmit = async (vals) => {
    try {
      if (editing) {
        await axiosInstance.put(`${config.endpoint}/${editing[config.idKey]}`, vals);
        toast.success(t('cima.panneau.modifie'));
      } else {
        await axiosInstance.post(config.endpoint, vals);
        toast.success(t('cima.panneau.cree'));
      }
      setModalOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.message || t('cima.panneau.erreurSauvegarde'));
    }
  };

  const remove = async (record) => {
    try {
      await axiosInstance.delete(`${config.endpoint}/${record[config.idKey]}`);
      toast.success(t('cima.panneau.supprime'));
      load();
    } catch (e) {
      toast.error(e?.response?.data?.message || t('cima.panneau.erreurSuppression'));
    }
  };

  /* Barème légal (art. 265, 266) : le calcul applique le texte du Code CIMA, et une offre
     ne peut pas descendre sous le barème. Le modifier ici n'aurait aucun effet sur les
     offres ; on l'affiche sans le rendre modifiable. */
  const lectureSeule = Boolean(config.baremeLegal);

  const cols = lectureSeule ? config.columns : [
    ...config.columns,
    {
      title: '', key: 'actions', width: 90, align: 'center', fixed: 'right',
      render: (_, r) => (
        <Space size={4}>
          <Tooltip title={t('cima.panneau.modifier')}>
            <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(r)} style={{ borderRadius: 6 }} />
          </Tooltip>
          <Popconfirm title={t('cima.panneau.supprimerConfirmation')} onConfirm={() => remove(r)} okText={t('cima.commun.oui')} cancelText={t('cima.commun.non')}>
            <Tooltip title={t('cima.panneau.supprimer')}>
              <Button size="small" danger icon={<DeleteOutlined />} style={{ borderRadius: 6 }} />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 14 }}>
        <div>
          <div style={{ fontWeight: 700, color: GREEN, fontSize: 15 }}>{config.title}</div>
          <div style={{ color: '#6b7280', fontSize: 12 }}>{config.description}</div>
          <div style={{ color: '#9ca3af', fontSize: 12, marginTop: 2 }}>
            {t('cima.panneau.nbEntrees', { count: data.length })}
          </div>
        </div>
        <Space>
          <Tooltip title={t('cima.panneau.actualiser')}>
            <Button icon={<ReloadOutlined />} onClick={load} style={{ borderRadius: 8 }} />
          </Tooltip>
          {!lectureSeule && (
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}
              style={{ background: GREEN, borderColor: GREEN, borderRadius: 8 }}>
              {t('cima.panneau.ajouter')}
            </Button>
          )}
        </Space>
      </div>

      {lectureSeule && (
        <Alert type="info" showIcon message={config.baremeLegal} style={{ marginBottom: 14, borderRadius: 8, fontSize: 12.5 }} />
      )}

      {data.length === 0 && !loading ? (
        <Empty description={t('cima.panneau.aucuneEntree')} style={{ padding: 32 }} />
      ) : (
        <Table
          className="cb-table"
          dataSource={data}
          columns={cols}
          rowKey={config.rowKey}
          loading={loading}
          size="small"
          pagination={{ pageSize: config.pageSize || 20, showTotal: (total) => t('cima.panneau.total', { count: total }) }}
          scroll={{ x: 900 }}
        />
      )}

      <CBModal
        title={editing ? t('cima.panneau.titreModification', { titre: config.title }) : t('cima.panneau.titreCreation', { titre: config.title })}
        open={modalOpen}
        onCancel={() => setModalOpen(false)}
        footer={null}
        width={680}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={onSubmit} style={{ marginTop: 16 }}>
          {config.fields(editing)}
          <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', borderTop: '1px solid #e5e7eb', paddingTop: 16 }}>
            <Button onClick={() => setModalOpen(false)}>{t('cima.panneau.annuler')}</Button>
            <Button type="primary" htmlType="submit"
              style={{ background: GREEN, borderColor: GREEN, borderRadius: 8 }}>
              {editing ? t('cima.panneau.boutonModifier') : t('cima.panneau.creer')}
            </Button>
          </div>
        </Form>
      </CBModal>
    </div>
  );
}

// ───────────────────────────────────────────────────────────────────
// Page principale avec onglets
// ───────────────────────────────────────────────────────────────────

export default function ParametresCima() {
  const { t } = useTranslation('parametres');
  const [activeTab, setActiveTab] = useState('capitalisation');

  const items = Object.values(tableBaremes(t)).map(cfg => ({
    key: cfg.key,
    label: cfg.title,
    children: <BaremePanel config={cfg} />,
  }));

  return (
    <Layout title={t('cima.page.titreOnglet')}>
      <div className="cb-page-header">
        <div>
          <h1 className="cb-page-title">
            <CalculatorOutlined style={{ marginRight: 8, color: GREEN }} />
            {t('cima.page.titre')}
          </h1>
        </div>
      </div>

      <Alert
        type="info" showIcon style={{ marginBottom: 16, borderRadius: 8 }}
        message={t('cima.rappel.titre')}
        description={
          <div style={{ fontSize: 13 }}>
            <Trans t={t} i18nKey="cima.rappel.description" components={{ 1: <b />, 3: <b />, 5: <code />, 7: <b /> }} />
          </div>
        }
      />

      <div className="cb-card" style={{ padding: 16 }}>
        <Tabs activeKey={activeTab} onChange={setActiveTab} items={items} destroyInactiveTabPane />
      </div>
    </Layout>
  );
}
